import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Key, Loader2, RefreshCw, Copy, Check, BarChart3 } from 'lucide-react';

export const SettingsPanel = ({ email, setEmail, apiKey, setApiKey, saveApiKey, loading, isSetup, testKey, testingKey, testResult, regenerateCode, regenerating, newAccessCode, copyNewCode, newCodeCopied, usage, goToUsage }) => (
  <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-panel">
    <Card className="card-tech p-8">
      <h2 className="text-2xl font-bold text-green-400 mb-2">
        <Key className="w-6 h-6 inline mr-2" /> Pro Settings
      </h2>
      <p className="text-green-300 mb-6">Configure your OpenAI API key to use AI features. Your key is stored securely and never shared.</p>
      <div className="space-y-4">
        <div>
          <label className="block text-green-400 text-sm font-semibold mb-2">Your Pro Email</label>
          <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email used for Pro purchase"
            className="bg-black/50 border-green-500/30 text-green-300 h-12" data-testid="pro-email-input" />
        </div>
        <div>
          <label className="block text-green-400 text-sm font-semibold mb-2">OpenAI API Key</label>
          <Input value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="sk-..." type="password"
            className="bg-black/50 border-green-500/30 text-green-300 h-12" data-testid="pro-apikey-input" />
          <p className="text-green-500/60 text-xs mt-2">Get your key at platform.openai.com/api-keys. Stored securely in database.</p>
        </div>
        <Button onClick={saveApiKey} disabled={loading} className="btn-primary w-full py-4" data-testid="save-apikey-button">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save API Key & Activate AI'}
        </Button>
        {isSetup && (
          <Button onClick={testKey} disabled={testingKey}
            className="btn-secondary w-full py-3" data-testid="test-apikey-button">
            {testingKey ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing key...</> : 'Test My OpenAI Key'}
          </Button>
        )}
      </div>
      {isSetup && (
        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded">
          <p className="text-green-400 text-sm font-semibold">API key saved! All AI features are now active.</p>
        </div>
      )}
      {testResult && (
        <div
          className={`mt-3 p-3 rounded border text-sm font-semibold ${
            testResult.ok
              ? 'bg-green-500/10 border-green-500/40 text-green-400'
              : 'bg-red-500/10 border-red-500/40 text-red-400'
          }`}
          data-testid="test-apikey-result"
        >
          {testResult.ok ? '✅ ' : '⚠️ '}{testResult.message}
        </div>
      )}
    </Card>

    {isSetup && usage && (
      <Card className="card-tech p-6" data-testid="settings-usage-summary">
        <div className="flex items-center justify-between gap-4 mb-3">
          <h3 className="text-lg font-bold text-green-400">
            <BarChart3 className="w-5 h-5 inline mr-2" /> Token Usage
          </h3>
          <button onClick={goToUsage} className="text-green-400 text-sm underline hover:text-green-300"
            data-testid="settings-usage-details-link">View details</button>
        </div>
        <div className="flex items-baseline gap-6">
          <div>
            <p className="text-3xl font-bold text-green-400">{(usage.total_tokens || 0).toLocaleString('en-US')}</p>
            <p className="text-green-500/70 text-xs uppercase tracking-wide">Total tokens</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-green-400">{(usage.total_requests || 0).toLocaleString('en-US')}</p>
            <p className="text-green-500/70 text-xs uppercase tracking-wide">AI requests</p>
          </div>
        </div>
      </Card>
    )}

    <Card className="card-tech p-6">
      <h3 className="text-lg font-bold text-green-400 mb-3">Access Code</h3>
      <p className="text-green-300 text-sm mb-4">
        Suspect your access code leaked? Rotate it instantly. The old code stops working right away
        and the new one is emailed to you.
      </p>
      <Button onClick={regenerateCode} disabled={regenerating} className="btn-secondary" data-testid="regenerate-code-btn">
        {regenerating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rotating...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate Access Code</>}
      </Button>
      {newAccessCode && (
        <div className="mt-4 p-4 border-2 border-yellow-500/50 bg-yellow-500/10 rounded" data-testid="new-access-code">
          <p className="text-yellow-400 font-bold text-sm mb-2">New Access Code — save it now:</p>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 text-yellow-300 font-mono bg-black/60 px-4 py-2 rounded break-all flex items-center" data-testid="new-access-code-value">{newAccessCode}</code>
            <Button onClick={copyNewCode} className="btn-secondary px-4 shrink-0" data-testid="copy-new-code-btn" aria-label="Copy new access code">
              {newCodeCopied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
            </Button>
          </div>
        </div>
      )}
    </Card>

    <Card className="card-tech p-6">
      <h3 className="text-lg font-bold text-green-400 mb-3">Security Information</h3>
      <ul className="space-y-2 text-green-300 text-sm">
        <li>- Your API key is stored encrypted in our database</li>
        <li>- Keys are never logged or exposed in responses</li>
        <li>- All AI requests use YOUR key (your billing, your control)</li>
        <li>- You can change or revoke your key anytime at OpenAI</li>
        <li>- We recommend setting usage limits in your OpenAI dashboard</li>
      </ul>
    </Card>
  </div>
);
